/* iter176 — Cigars & Bars sheet (Ride Mode).
 *
 * Modeled on SavedPlacesSheet visual style: dark bottom sheet, gold
 * accents, tap-a-row-to-start-a-ride, gold "Show on Map" footer that
 * opens the map filtered to businesses with `show_on_ride=true`.
 *
 * Data source: GET /api/businesses — client-side filter on
 * `show_on_ride === true`. When zero rows exist the sheet still opens
 * with an empty-state message so admins get a clear signal that they
 * need to flip the toggle on some businesses.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Cigarette, Loader2 } from "lucide-react";
import { api, getMe } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useGps } from "../lib/useGps";
import { haversineKm } from "../lib/geo";
import { sendRideRequest } from "../lib/sendRideRequest";

export default function CigarsBarsSheet({ isOpen, onClose }) {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);
  const coords = useGps();

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    // iter184 — Track auth state so we can show the guest-only sign-in
    // CTA in the footer without any nagging for returning users.
    getMe().then((me) => { if (!cancelled) setIsGuest(!me); }).catch(() => {});
    (async () => {
      try {
        const listRes = await api.get("/businesses").catch(() => ({ data: [] }));
        if (cancelled) return;
        const all = Array.isArray(listRes.data) ? listRes.data : [];
        const filtered = all.filter((b) => b && b.show_on_ride === true && b.lat != null && b.lng != null);
        setRows(filtered);
      } catch { /* keep prior state */ } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  const sortedRows = useMemo(() => {
    if (!coords) return rows;
    return [...rows].sort((a, b) => {
      const da = haversineKm(coords, { lat: a.lat, lng: a.lng });
      const db = haversineKm(coords, { lat: b.lat, lng: b.lng });
      // iter176-fix — haversineKm can return null when either point is
      // invalid. Sort those to the end.
      return (da == null ? Infinity : da) - (db == null ? Infinity : db);
    });
  }, [rows, coords]);

  const startRide = (biz) => {
    // Reuse the shared ride-request flow so WhatsApp deep-linking,
    // fallback numbers, and language handling all stay consistent.
    sendRideRequest(
      {
        name: biz.name,
        address: biz.address || biz.neighborhood || "",
        lat: biz.lat,
        lng: biz.lng,
      },
      { businessId: biz.id, source: "cigars-bars-sheet" },
    );
    onClose?.();
  };

  if (!isOpen) return null;
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      data-testid="cigars-bars-sheet"
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "flex-end",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#171717",
          borderTop: "1px solid rgba(232,184,75,0.28)",
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
          width: "100%", maxHeight: "82vh",
          display: "flex", flexDirection: "column",
          fontFamily: "'DM Sans', sans-serif",
          color: "#F5F1E8",
        }}
      >
        <header className="flex items-center justify-between px-5 pt-5 pb-3">
          <h2
            data-testid="cigars-bars-title"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#F5F1E8", lineHeight: 1 }}
          >
            {lang === "es" ? "Cigarros y Bares" : "Cigars & Bars"}
          </h2>
          <button
            type="button"
            data-testid="cigars-bars-close"
            onClick={onClose}
            aria-label={lang === "es" ? "Cerrar" : "Close"}
            className="inline-flex items-center justify-center"
            style={{ width: 44, height: 44, borderRadius: 9999, background: "transparent", color: "#F5F1E8" }}
          >
            <X className="w-5 h-5" strokeWidth={1.6} />
          </button>
        </header>

        <div className="overflow-y-auto flex-1 px-2 pb-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: "#B0AAA3" }} />
            </div>
          ) : sortedRows.length === 0 ? (
            <div className="text-center py-10 px-4" style={{ color: "#B0AAA3", fontSize: 14 }}>
              {lang === "es"
                ? "No hay cigarros o bares marcados para el modo Viaje todavía."
                : "No cigars or bars marked for Ride mode yet."}
            </div>
          ) : (
            <ul className="space-y-1">
              {sortedRows.map((biz) => {
                const km = coords ? haversineKm(coords, { lat: biz.lat, lng: biz.lng }) : null;
                const distance = Number.isFinite(km) ? `${km.toFixed(1)} km` : "";
                return (
                  <li key={biz.id}>
                    <button
                      type="button"
                      data-testid={`cigars-bars-row-${biz.id}`}
                      onClick={() => startRide(biz)}
                      className="w-full flex items-center gap-3 text-left px-3"
                      style={{
                        minHeight: 60,
                        borderRadius: 10,
                        background: "transparent",
                        color: "#F5F1E8",
                      }}
                    >
                      <span
                        className="inline-flex items-center justify-center shrink-0"
                        style={{ width: 36, height: 36, borderRadius: 9999, background: "rgba(232,184,75,0.12)" }}
                      >
                        <Cigarette className="w-4 h-4" strokeWidth={1.6} color="#E8B84B" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block truncate" style={{ fontSize: 15, fontWeight: 500 }}>
                          {biz.name}
                        </span>
                        <span
                          className="block truncate"
                          style={{ fontSize: 12, color: "rgba(126,120,109,0.9)" }}
                        >
                          {distance}{distance && biz.neighborhood ? " · " : ""}{biz.neighborhood || ""}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer
          className="px-5 py-4 border-t"
          style={{ borderColor: "rgba(232,184,75,0.18)", background: "#171717" }}
        >
          <button
            type="button"
            data-testid="cigars-bars-show-on-map"
            onClick={() => { onClose?.(); navigate("/map?ridefilter=cigars"); }}
            className="w-full inline-flex items-center justify-center gap-2"
            style={{
              minHeight: 48,
              borderRadius: 12,
              background: "linear-gradient(135deg, #E8B84B, #B08C36)",
              color: "#0B0B0B",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 15,
              fontWeight: 600,
              border: "none",
              letterSpacing: "0.02em",
            }}
          >
            {lang === "es" ? "Ver en Mapa" : "Show on Map"}
          </button>
          {/* iter184 — Guest-only auth CTA. */}
          {isGuest && (
            <button
              type="button"
              data-testid="cigars-bars-signin"
              onClick={() => { onClose?.(); navigate("/login"); }}
              className="w-full inline-flex items-center justify-center gap-2 mt-3"
              style={{
                minHeight: 48,
                borderRadius: 12,
                background: "transparent",
                color: "#E8B84B",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 15,
                fontWeight: 600,
                border: "1px solid rgba(232,184,75,0.55)",
                letterSpacing: "0.02em",
              }}
            >
              {lang === "es" ? "Crear cuenta / Iniciar sesión" : "Create Account / Log In"}
            </button>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}
